import React, { useState, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Alert,
  Skeleton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  LinearProgress,
  Button
} from "@mui/material"
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle,
  PendingActions,
  PlayArrow,
  Assignment,
  Inventory,
  TrendingUp,
  FilterList
} from "@mui/icons-material"
import { useGetAuditQuery, AuditProductStatus, AuditStatus } from "src/graphql/quark/quark.type"
import ProductLotsModal from "./ProductLotsModal"
import { ChipStyled } from "src/shared/components"

interface AuditDetailModalProps {
  open: boolean
  onClose: () => void
  auditId: string
}

interface AuditProduct {
  id: string
  name?: string | null
  secondaryId?: number | null
  status: AuditProductStatus
  lotCount: number
}

const getStatusIcon = (status: AuditProductStatus) => {
  switch (status) {
    case AuditProductStatus.Pending:
      return <PendingActions color="warning" />
    case AuditProductStatus.InProgress:
      return <PlayArrow color="info" />
    case AuditProductStatus.Completed:
      return <CheckCircle color="success" />
    default:
      return <Assignment color="action" />
  }
}

const getStatusColor = (status: AuditProductStatus) => {
  switch (status) {
    case AuditProductStatus.Pending:
      return "warning"
    case AuditProductStatus.InProgress:
      return "info"
    case AuditProductStatus.Completed:
      return "success"
    default:
      return "default"
  }
}

const AuditDetailModal: React.FC<AuditDetailModalProps> = ({ open, onClose, auditId }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [selectedProduct, setSelectedProduct] = useState<AuditProduct | null>(null)

  const { data, loading, error } = useGetAuditQuery({
    variables: { auditId },
    skip: !open || !auditId,
    fetchPolicy: "cache-and-network"
  })

  const audit = data?.getAudit
  const auditProducts = useMemo(() => {
    return (
      audit?.auditProducts?.map((ap) => ({
        id: ap.id,
        name: ap.product.name,
        secondaryId: ap.product.secondaryId,
        status: ap.status,
        lotCount: ap.lotCount
      })) || []
    )
  }, [audit?.auditProducts])

  // Filter products based on search term and status
  const filteredProducts = useMemo(() => {
    return auditProducts.filter((product) => {
      const matchesSearch = !searchTerm || product.name?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesStatus = statusFilter === "all" || product.status === statusFilter
      return matchesSearch && matchesStatus
    })
  }, [auditProducts, searchTerm, statusFilter])

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalProducts = auditProducts.length
    const completedProducts = auditProducts.filter((p) => p.status === AuditProductStatus.Completed).length
    const inProgressProducts = auditProducts.filter((p) => p.status === AuditProductStatus.InProgress).length
    const pendingProducts = auditProducts.filter((p) => p.status === AuditProductStatus.Pending).length
    const progressPercentage = totalProducts > 0 ? Math.round((completedProducts / totalProducts) * 100) : 0

    return {
      totalProducts,
      completedProducts,
      inProgressProducts,
      pendingProducts,
      progressPercentage
    }
  }, [auditProducts])

  const handleProductClick = (product: AuditProduct) => {
    setSelectedProduct(product)
  }

  const handleCloseProductModal = () => {
    setSelectedProduct(null)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setStatusFilter("all")
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Alert severity="error">Failed to load audit details. Please try again.</Alert>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth sx={{ "& .MuiDialog-paper": { height: "90vh" } }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" component="div" fontWeight="bold">
                {loading ? <Skeleton width={200} /> : `Audit Details - #${audit?.secondaryId}`}
              </Typography>
              {!loading && audit && (
                <Box display="flex" alignItems="center" gap={2} mt={1}>
                  <Typography variant="body2" color="text.secondary">
                    Audit ID: {audit.id}
                  </Typography>
                  <Chip
                    label={audit.status}
                    size="small"
                    color={audit.status === AuditStatus.Completed ? "success" : "primary"}
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box>
              {/* Summary Cards Skeleton */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[1, 2, 3, 4].map((index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Card>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Skeleton variant="circular" width={32} height={32} sx={{ mx: "auto", mb: 1 }} />
                        <Skeleton variant="text" width={60} height={24} sx={{ mx: "auto" }} />
                        <Skeleton variant="text" width={100} height={16} sx={{ mx: "auto" }} />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Search and Filter Skeleton */}
              <Box sx={{ mb: 3 }}>
                <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={40} />
              </Box>

              {/* Products List Skeleton */}
              <List>
                {[1, 2, 3].map((index) => (
                  <ListItem key={index}>
                    <Card sx={{ width: "100%" }}>
                      <CardContent>
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </CardContent>
                    </Card>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Box>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Inventory color="primary" sx={{ fontSize: 32, mb: 1 }} />
                      <Typography variant="h6">{summaryStats.totalProducts}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Products
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <CheckCircle color="success" sx={{ fontSize: 32, mb: 1 }} />
                      <Typography variant="h6">{summaryStats.completedProducts}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Completed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <PlayArrow color="info" sx={{ fontSize: 32, mb: 1 }} />
                      <Typography variant="h6">{summaryStats.inProgressProducts}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        In Progress
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <TrendingUp
                        color={
                          summaryStats.progressPercentage >= 75
                            ? "success"
                            : summaryStats.progressPercentage >= 50
                              ? "warning"
                              : "error"
                        }
                        sx={{ fontSize: 32, mb: 1 }}
                      />
                      <Typography variant="h6">{summaryStats.progressPercentage}%</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Progress
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Overall Progress
                  </Typography>
                  <Typography variant="body2" color="primary.main">
                    {summaryStats.completedProducts}/{summaryStats.totalProducts} completed
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={summaryStats.progressPercentage}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>

              {/* Search and Filter Controls */}
              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6">Products ({filteredProducts.length})</Typography>
                  {(searchTerm || statusFilter !== "all") && (
                    <Button variant="outlined" size="small" onClick={clearFilters} startIcon={<FilterList />}>
                      Clear Filters
                    </Button>
                  )}
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    fullWidth
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                    size="small"
                  />
                  <FormControl sx={{ minWidth: 180 }} size="small">
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Filter by Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value={AuditProductStatus.Pending}>Pending</MenuItem>
                      <MenuItem value={AuditProductStatus.InProgress}>In Progress</MenuItem>
                      <MenuItem value={AuditProductStatus.Completed}>Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Box>

              {/* Products List */}
              <Box>
                {filteredProducts.length === 0 ? (
                  <Alert severity="info">No products found matching your search criteria.</Alert>
                ) : (
                  <List sx={{ maxHeight: "400px", overflow: "auto" }}>
                    {filteredProducts.map((product) => (
                      <ListItem key={product.id} disablePadding sx={{ mb: 1 }}>
                        <Card sx={{ width: "100%", "&:hover": { boxShadow: 2 } }}>
                          <ListItemButton onClick={() => handleProductClick(product)} sx={{ p: 2 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>{getStatusIcon(product.status)}</ListItemIcon>
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {product.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    ID: {product.secondaryId} • {product.lotCount} lots
                                  </Typography>
                                </Box>
                                <ChipStyled
                                  label={product.status.toLowerCase().replace("_", " ")}
                                  size="small"
                                  color={getStatusColor(product.status) as any}
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          </ListItemButton>
                        </Card>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Lots Modal */}
      {selectedProduct && (
        <ProductLotsModal
          open={!!selectedProduct}
          onClose={handleCloseProductModal}
          auditProductId={selectedProduct.id}
          productName={selectedProduct.name || "Unknown Product"}
        />
      )}
    </>
  )
}

export default AuditDetailModal
